import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Droplets, ArrowRight, Plus, Link2, CheckCircle2, Clock, ExternalLink,
  RefreshCw, Copy, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Iter 427.p34 — Tools → Water Purification Information Sheet.
 *
 * Visual parity with ToolsOnboarding.jsx: hero + search + "New sheet"
 * button, grouped rows for Submitted / In progress / Shared (public
 * link open) / Reviewed. Each row shows business name, submitter,
 * submitted-at, status chip, and (when applicable) a customer link
 * + copy-share-link button.
 */
export default function ToolsWaterSheet() {
  const nav = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/water-info-sheets");
      setSheets(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load sheets.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return sheets;
    return sheets.filter((sh) => (
      (sh.title || "").toLowerCase().includes(s) ||
      (sh.business_name || "").toLowerCase().includes(s) ||
      (sh.data?.["site.contact_email"] || "").toLowerCase().includes(s)
    ));
  }, [sheets, q]);

  const groups = useMemo(() => {
    const out = { reviewed: [], submitted: [], shared: [], draft: [] };
    for (const s of filtered) {
      const st = s.status || "draft";
      if (out[st]) out[st].push(s);
      else out.draft.push(s);
    }
    return out;
  }, [filtered]);

  const createBlank = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/water-info-sheets", {
        title: `Water sheet — ${new Date().toLocaleDateString("en-GB")}`,
      });
      nav(`/tools/water-info-sheet/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create sheet.");
    } finally {
      setCreating(false);
    }
  };

  const copyShareLink = async (sh) => {
    try {
      let token = sh.share_token;
      let path = token ? `/public/water-info-sheet/${token}` : null;
      if (!token || sh.share_revoked) {
        const { data } = await api.post(`/water-info-sheets/${sh.id}/share-link`);
        token = data.token;
        path = data.public_path;
      }
      const url = `${window.location.origin}${path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied to clipboard.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create share link.");
    }
  };

  const deleteSheet = async (sh) => {
    if (!window.confirm(`Delete "${sh.title || sh.business_name || sh.id}"? This can't be undone.`)) return;
    try {
      await api.delete(`/water-info-sheets/${sh.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-16">
      {/* ---- Hero header ---- */}
      <header className="hv-card p-4 md:p-6" data-testid="tools-water-sheet-hero">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight flex items-center gap-3 flex-wrap">
              <Droplets className="w-7 h-7 text-[#1FB6C1]" />
              Water Purification Info Sheet
              <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#1FB6C1]">
                Public link ready
              </span>
            </h1>
            <div className="text-sm text-[#a0aabf] mt-1">
              A lean sizing fact-find. Share the public link with a prospect and the completed submission drops into this list — no login required for the customer.
            </div>
          </div>
          <button
            type="button"
            onClick={createBlank}
            disabled={creating}
            className="hv-btn hv-btn-primary text-sm"
            data-testid="tools-water-sheet-new-btn"
          >
            <Plus className="w-4 h-4" /> New sheet
          </button>
        </div>
      </header>

      {/* ---- Search ---- */}
      <div className="hv-card p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#647087]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by business, title or contact email…"
          className="hv-input flex-1 text-sm"
          data-testid="tools-water-sheet-search"
        />
      </div>

      {loading ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]">Loading…</div>
      ) : (
        <>
          <SheetGroup
            title="Submitted — awaiting review"
            icon={<Clock className="w-4 h-4 text-amber-300" />}
            rows={groups.submitted}
            testId="tools-water-sheet-group-submitted"
            onOpen={(sh) => nav(`/tools/water-info-sheet/${sh.id}`)}
            onCopy={copyShareLink}
            onDelete={deleteSheet}
          />
          <SheetGroup
            title="Shared — waiting for customer"
            icon={<Link2 className="w-4 h-4 text-cyan-300" />}
            rows={groups.shared}
            testId="tools-water-sheet-group-shared"
            onOpen={(sh) => nav(`/tools/water-info-sheet/${sh.id}`)}
            onCopy={copyShareLink}
            onDelete={deleteSheet}
          />
          <SheetGroup
            title="Draft — not yet shared"
            icon={<Plus className="w-4 h-4 text-[#a0aabf]" />}
            rows={groups.draft}
            testId="tools-water-sheet-group-draft"
            onOpen={(sh) => nav(`/tools/water-info-sheet/${sh.id}`)}
            onCopy={copyShareLink}
            onDelete={deleteSheet}
          />
          <SheetGroup
            title="Reviewed & closed"
            icon={<CheckCircle2 className="w-4 h-4 text-emerald-300" />}
            rows={groups.reviewed}
            testId="tools-water-sheet-group-reviewed"
            onOpen={(sh) => nav(`/tools/water-info-sheet/${sh.id}`)}
            onCopy={copyShareLink}
            onDelete={deleteSheet}
          />
        </>
      )}
    </div>
  );
}

function SheetGroup({ title, icon, rows, testId, onOpen, onCopy, onDelete }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="space-y-2" data-testid={testId}>
      <h2 className="text-xs uppercase tracking-wider text-[#647087] font-bold flex items-center gap-2">
        {icon} {title} <span className="text-[10px] text-[#647087]">· {rows.length}</span>
      </h2>
      <div className="space-y-2">
        {rows.map((sh) => (
          <SheetRow
            key={sh.id}
            sh={sh}
            onOpen={() => onOpen(sh)}
            onCopy={() => onCopy(sh)}
            onDelete={() => onDelete(sh)}
          />
        ))}
      </div>
    </section>
  );
}

function SheetRow({ sh, onOpen, onCopy, onDelete }) {
  const dataObj = sh.data || {};
  const contact = dataObj["site.contact_name"] || "—";
  const email = dataObj["site.contact_email"] || "";
  const business = sh.business_name || dataObj["site.business_name"] || "Untitled";
  const submittedAt = sh.submitted_at ? new Date(sh.submitted_at).toLocaleString("en-GB") : null;
  return (
    <div
      className="hv-card p-3 flex items-center gap-3 hover:border-[#1FB6C1]/40 transition"
      data-testid={`tools-water-sheet-row-${sh.id}`}
    >
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onOpen}
          className="text-left w-full"
          data-testid={`tools-water-sheet-open-${sh.id}`}
        >
          <div className="font-medium text-sm truncate">{business}</div>
          <div className="text-[11px] text-[#647087] truncate">
            {contact}{email ? ` · ${email}` : ""}
            {submittedAt ? ` · submitted ${submittedAt}` : ""}
          </div>
        </button>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onCopy}
          className="hv-btn hv-btn-ghost text-xs"
          title="Copy public share link"
          data-testid={`tools-water-sheet-copy-${sh.id}`}
        >
          <Copy className="w-3 h-3" /> {sh.share_token && !sh.share_revoked ? "Copy link" : "Get link"}
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`tools-water-sheet-review-${sh.id}`}
        >
          Open <ArrowRight className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="hv-btn hv-btn-ghost text-xs text-rose-400 hover:text-rose-300"
          title="Delete sheet"
          data-testid={`tools-water-sheet-delete-${sh.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
